
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { locationService } from '@/services/locationService';
import { toast } from '@/hooks/use-toast';

interface LocationData {
  latitude: number;
  longitude: number;
  lat: number;
  lng: number;
  city?: string;
  address?: string;
  country?: string;
}

interface GeolocationState {
  location: LocationData | null;
  error: string | null;
  isLoading: boolean;
  permission: PermissionState;
  hasLocation: boolean;
}

export const useGeolocation = () => {
  const [state, setState] = useState<GeolocationState>({
    location: null,
    error: null,
    isLoading: false,
    permission: 'prompt',
    hasLocation: false
  });

  // Check permission status
  const checkPermission = useCallback(async () => {
    try {
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        setState(prev => ({ ...prev, permission: permission.state }));
        
        permission.onchange = () => {
          setState(prev => ({ ...prev, permission: permission.state }));
        };
      }
    } catch (error) {
      console.error('Permission check failed:', error);
    }
  }, []);

  // Get current location with enhanced data
  const getCurrentLocation = useCallback(async (updateProfile = false) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    if (!navigator.geolocation) {
      const error = 'Geolocation is not supported by this browser';
      setState(prev => ({
        ...prev,
        error,
        isLoading: false,
        permission: 'denied'
      }));
      return;
    }

    try {
      const locationData = await locationService.getCurrentLocation();
      
      // Create enhanced location object with both formats
      const enhancedLocation: LocationData = {
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        lat: locationData.latitude,
        lng: locationData.longitude,
        city: locationData.city,
        address: locationData.address,
        country: locationData.country
      };

      setState(prev => ({
        ...prev,
        location: enhancedLocation,
        error: null,
        isLoading: false,
        hasLocation: true,
        permission: 'granted'
      }));

      // Save to user profile if requested
      if (updateProfile) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await locationService.saveUserLocation(user.id, locationData);
          toast({
            title: "Location Updated",
            description: "Your location has been saved to your profile"
          });
        }
      }

    } catch (error: any) {
      let errorMessage = 'Failed to get location';
      let permission: PermissionState = 'prompt';
      
      if (error.message.includes('denied')) {
        errorMessage = 'Location access denied by user';
        permission = 'denied';
      } else if (error.message.includes('unavailable')) {
        errorMessage = 'Location information unavailable';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Location request timed out';
      }

      setState(prev => ({
        ...prev,
        location: null,
        error: errorMessage,
        isLoading: false,
        hasLocation: false,
        permission
      }));

      toast({
        title: "Location Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  }, []);

  // Share location temporarily for messaging
  const shareLocationTemporarily = useCallback(async (durationSeconds = 3600) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      await locationService.shareLocationTemporarily(user.id, durationSeconds);
      
      toast({
        title: "Location Shared",
        description: `Your location will be shared for ${Math.round(durationSeconds / 60)} minutes`
      });
    } catch (error: any) {
      toast({
        title: "Sharing Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  }, []);

  // Request location (backwards compatibility)
  const requestLocation = useCallback(() => {
    getCurrentLocation(false);
  }, [getCurrentLocation]);

  // Initialize permission check
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  return {
    ...state,
    getCurrentLocation,
    shareLocationTemporarily,
    requestLocation
  };
};
